// MF-399 — Rounds/milestones configuration (builder side).
// Diálogo para configurar la lista de rounds de un assessment (label + fechas)
// y el flag carry-forward. Sigue el patrón de GlobalReviewers: un botón en el
// header abre un DialogConfirm con el editor. Los rounds viven en el estado del
// builder (assessmentsBuilder.rounds) y se envían tal cual al BE (que asigna
// _id/order/status en normalizeRounds). Ver docs/design/MF-399-assessment-rounds.md
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { FormControlLabel } from '@mui/material'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import InputDateUTC from '../../../../FormControl/input/InputDateUTC'
import { updateAssessmentBuilder } from '../../../../../store/actions/dataEntryAssessmentBuilderActions'

const ROUND_STATUS_FINALIZED = 'finalized'

const emptyRound = () => ({ label: '', sittingDate: null, dueDate: null })

const RoundsBuilder = ({ disabled = false }) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [rounds, setRounds] = useState([])
  const [carryForward, setCarryForward] = useState(true)
  const [rowErrors, setRowErrors] = useState([])

  const savedRounds = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.rounds ?? []
  )
  const savedCarryForward = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.carryForward ?? true
  )

  // Al abrir, sembramos el estado local desde redux (así Cancel descarta cambios).
  useEffect(() => {
    if (open) {
      setRounds(savedRounds?.length ? savedRounds.map((r) => ({ ...r })) : [])
      setCarryForward(savedCarryForward ?? true)
      setRowErrors([])
    }
  }, [open])

  const isFinalized = (round) => round?.status === ROUND_STATUS_FINALIZED

  const onAddRound = () => {
    setRounds((prev) => [...prev, emptyRound()])
  }

  const onRemoveRound = (index) => {
    setRounds((prev) => prev.filter((_, i) => i !== index))
    setRowErrors((prev) => prev.filter((_, i) => i !== index))
  }

  const onChangeRound = (index, field, value) => {
    setRounds((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)))
    if (rowErrors[index]?.[field]) {
      setRowErrors((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: null } : e)))
    }
  }

  const validate = () => {
    const errors = rounds.map((r) => {
      const e = {}
      if (!r.label || !String(r.label).trim().length) e.label = 'Required'
      if (!r.sittingDate) e.sittingDate = 'Required'
      return e
    })
    setRowErrors(errors)
    return errors.every((e) => !e.label && !e.sittingDate)
  }

  const onConfirm = () => {
    if (rounds.length && !validate()) return
    dispatch(
      updateAssessmentBuilder({
        rounds: rounds.map((r, i) => ({ ...r, order: i + 1 })),
        carryForward
      })
    )
    setOpen(false)
  }

  const buttonTitle = savedRounds?.length ? `Rounds (${savedRounds.length})` : 'Configure rounds'

  return (
    <>
      <Box className='flex items-center'>
        <IconTooltipButton
          title={buttonTitle}
          disabled={disabled}
          icon={'layer-group'}
          className={'shadow-sm'}
          onClick={() => setOpen(true)}
        />
        {savedRounds?.length > 0 && (
          <Chip size='small' color='primary' variant='outlined' label={savedRounds.length} className='ml-1' />
        )}
      </Box>

      <DialogConfirmCancel
        id={`configure-assessment-rounds-modal`}
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={onConfirm}
        textConfirm='Apply'
        title={'Configure rounds'}
        onCloseHeader={true}
        maxWidth='md'
        scroll='body'
      >
        <Box className='flex flex-col gap-4' style={{ minWidth: '560px', paddingTop: 4 }}>
          <Typography variant='body2' color='textSecondary'>
            Split this assessment into rounds (milestones). Each round has its own sitting date and is marked
            separately. Leave empty to keep a single (legacy) assessment using the global dates.
          </Typography>

          <FormControlLabel
            className='m-0'
            control={
              <Switch
                checked={carryForward}
                onChange={(e) => setCarryForward(e.target.checked)}
                color='primary'
                size='small'
              />
            }
            label={
              <Typography variant='body2'>
                Carry forward marks — a new round starts pre-filled with the previous round&apos;s marks
              </Typography>
            }
          />

          <Divider />

          {rounds.length === 0 ? (
            <Typography variant='body2' color='textSecondary' className='py-4 text-center'>
              No rounds yet. Add one to split this assessment into milestones.
            </Typography>
          ) : (
            <Box className='flex flex-col gap-2'>
              {rounds.map((round, index) => {
                const finalized = isFinalized(round)
                return (
                  <Box
                    key={round?._id || `new-${index}`}
                    className='flex items-start gap-4 rounded'
                    style={{ padding: '10px 12px', background: index % 2 ? '#f7f9fb' : '#fff' }}
                  >
                    <Box style={{ width: '34%' }}>
                      <TextField
                        label={`Round ${index + 1} label*`}
                        value={round.label ?? ''}
                        error={rowErrors[index]?.label ? true : false}
                        helperText={rowErrors[index]?.label}
                        disabled={finalized}
                        onChange={(e) => onChangeRound(index, 'label', e.target.value)}
                        size='small'
                        variant='standard'
                        fullWidth
                      />
                    </Box>
                    <Box style={{ width: '27%' }}>
                      <InputDateUTC
                        label='Sitting date*'
                        value={round.sittingDate ?? null}
                        error={rowErrors[index]?.sittingDate ? true : false}
                        helperText={rowErrors[index]?.sittingDate}
                        disabled={finalized}
                        onChange={(value) => onChangeRound(index, 'sittingDate', value)}
                        size='small'
                        variant='standard'
                      />
                    </Box>
                    <Box style={{ width: '27%' }}>
                      <InputDateUTC
                        label='Due date'
                        value={round.dueDate ?? null}
                        disabled={finalized}
                        onChange={(value) => onChangeRound(index, 'dueDate', value)}
                        size='small'
                        variant='standard'
                        isStartOfTheDay={false}
                      />
                    </Box>
                    <Box style={{ width: '12%' }} className='flex items-center justify-center' sx={{ pt: 2 }}>
                      {finalized ? (
                        <Tooltip title='Finalized rounds cannot be edited or removed'>
                          <Chip size='small' color='success' label='Finalized' />
                        </Tooltip>
                      ) : (
                        <Tooltip title='Remove round'>
                          <IconButton size='small' color='error' onClick={() => onRemoveRound(index)}>
                            <FontAwesomeIcon icon={['fal', 'trash-alt']} size='sm' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}

          <Box className='flex justify-start pt-1'>
            <ButtonStartIcon
              startIcon={['fal', 'plus']}
              onClick={onAddRound}
              color='primary'
              variant='outlined'
            >
              Add round
            </ButtonStartIcon>
          </Box>
        </Box>
      </DialogConfirmCancel>
    </>
  )
}

export default RoundsBuilder
